import React, { useState } from 'react';
import { Users, ChevronLeft, Plus, X, User, Calendar, CheckSquare, Save } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
export function EditCommittee() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const committeeId = parseInt(id || '1');
  // Determine if this is a parent committee or sub-committee based on ID
  const isParentCommittee = committeeId < 100;
  const isSubCommittee = committeeId >= 100 && committeeId < 1000;
  // Default committee type based on ID
  const defaultCommitteeType = isParentCommittee ? 'parent' : isSubCommittee ? 'sub' : 'standard';
  // State for committee type
  const [committeeType, setCommitteeType] = useState<'standard' | 'parent' | 'sub'>(defaultCommitteeType);
  // State for parent committee (if this is a sub-committee)
  const [parentCommittee, setParentCommittee] = useState(isSubCommittee ? Math.floor(committeeId / 100).toString() : '');
  // Mock committee data for editing
  const committee = {
    id: committeeId,
    name: isParentCommittee ? committeeId === 1 ? 'Governance Committee' : committeeId === 2 ? 'Executive Committee' : 'Audit Committee' : isSubCommittee ? committeeId === 101 ? 'Board Governance Team' : committeeId === 102 ? 'Regulatory Compliance Team' : committeeId === 103 ? 'Shareholder Relations Team' : 'Team' : 'Finance Committee',
    description: isParentCommittee ? 'Oversees corporate governance matters, board effectiveness, and ensures compliance with regulatory requirements.' : isSubCommittee ? 'Team responsible for specific governance tasks and initiatives.' : 'Responsible for overseeing financial reporting, budgeting, and audit processes within the organization.',
    startDate: '2023-01-15',
    endDate: '2023-12-31',
    status: 'active',
    frequency: 'monthly',
    members: [{
      id: 1,
      name: 'Mohammed Al Hashimi',
      role: 'Chairperson',
      department: 'Finance',
      avatar: 'M'
    }, {
      id: 2,
      name: 'Aisha Al Mansoori',
      role: 'Secretary',
      department: 'Finance',
      avatar: 'A'
    }, {
      id: 3,
      name: 'Omar Al Shamsi',
      role: 'Member',
      department: 'Operations',
      avatar: 'O'
    }]
  };
  const [members, setMembers] = useState(committee.members);
  const [name, setName] = useState(committee.name);
  const [description, setDescription] = useState(committee.description);
  const [startDate, setStartDate] = useState(committee.startDate);
  const [endDate, setEndDate] = useState(committee.endDate);
  const [status, setStatus] = useState(committee.status);
  const [frequency, setFrequency] = useState(committee.frequency);
  const departments = ['Finance', 'Human Resources', 'Information Technology', 'Operations', 'Marketing', 'Legal', 'Research & Development', 'Customer Service', 'Sales', 'Procurement'];
  const roles = ['Chairperson', 'Secretary', 'Member'];
  const parentCommittees = [{
    id: 1,
    name: 'Governance Committee'
  }, {
    id: 2,
    name: 'Executive Committee'
  }, {
    id: 3,
    name: 'Audit Committee'
  }];
  const availableUsers = [{
    id: 1,
    name: 'Mohammed Al Hashimi',
    department: 'Finance'
  }, {
    id: 2,
    name: 'Aisha Al Mansoori',
    department: 'Human Resources'
  }, {
    id: 3,
    name: 'Omar Al Shamsi',
    department: 'Information Technology'
  }, {
    id: 4,
    name: 'Fatima Al Zaabi',
    department: 'Operations'
  }, {
    id: 5,
    name: 'Ahmed Al Dhaheri',
    department: 'Finance'
  }, {
    id: 6,
    name: 'Noura Al Suwaidi',
    department: 'Marketing'
  }, {
    id: 7,
    name: 'Khalid Al Mazrouei',
    department: 'Legal'
  }, {
    id: 8,
    name: 'Maryam Al Nuaimi',
    department: 'Human Resources'
  }];
  const addMember = (user: {
    id: number;
    name: string;
    department: string;
  }, role: string) => {
    const newMember = {
      id: user.id,
      name: user.name,
      role: role,
      department: user.department,
      avatar: user.name.charAt(0)
    };
    setMembers([...members, newMember]);
  };
  const removeMember = (id: number) => {
    setMembers(members.filter(member => member.id !== id));
  };
  // Filter out users who are already added as members
  const filteredUsers = availableUsers.filter(user => !members.some(member => member.id === user.id));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would save the committee data to a backend
    console.log({
      id: committeeId,
      name,
      description,
      type: committeeType,
      parentId: committeeType === 'sub' ? parseInt(parentCommittee) : null,
      startDate,
      endDate,
      status,
      frequency,
      members
    });
    // Redirect to committee detail page
    window.location.href = `/committees/${committeeId}`;
  };
  return <div data-id="element-667">
      <div className="flex items-center mb-6" data-id="element-668">
        <Link to={`/committees/${committeeId}`} className="flex items-center text-gray-600 hover:text-gray-900 mr-4" data-id="element-669">
          <ChevronLeft size={20} className="mr-1" data-id="element-670" />
          Back to Committee
        </Link>
        <h1 className="text-2xl font-bold" data-id="element-671">Edit Committee</h1>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-672">
        <form onSubmit={handleSubmit} data-id="element-673">
          <div className="space-y-6" data-id="element-674">
            {/* Committee Type Selection */}
            <div data-id="element-675">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-676">
                Committee Type <span className="text-red-500" data-id="element-677">*</span>
              </label>
              <div className="flex space-x-4" data-id="element-678">
                <label className="flex items-center" data-id="element-679">
                  <input type="radio" name="committeeType" value="standard" checked={committeeType === 'standard'} onChange={() => setCommitteeType('standard')} className="mr-2" data-id="element-680" />
                  Standard Committee
                </label>
                <label className="flex items-center" data-id="element-681">
                  <input type="radio" name="committeeType" value="parent" checked={committeeType === 'parent'} onChange={() => setCommitteeType('parent')} className="mr-2" data-id="element-682" />
                  Parent Committee
                </label>
                <label className="flex items-center" data-id="element-683">
                  <input type="radio" name="committeeType" value="sub" checked={committeeType === 'sub'} onChange={() => setCommitteeType('sub')} className="mr-2" data-id="element-684" />
                  Sub-Committee
                </label>
              </div>
            </div>

            {/* Parent Committee Selection (for sub-committees) */}
            {committeeType === 'sub' && <div data-id="element-685">
                <label htmlFor="parentCommittee" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-686">
                  Parent Committee <span className="text-red-500" data-id="element-687">*</span>
                </label>
                <select id="parentCommittee" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" value={parentCommittee} onChange={e => setParentCommittee(e.target.value)} required data-id="element-688">
                  <option value="" data-id="element-689">Select Parent Committee</option>
                  {parentCommittees.map(committee => <option key={committee.id} value={committee.id.toString()} data-id="element-690">
                      {committee.name}
                    </option>)}
                </select>
              </div>}

            {/* Committee Name */}
            <div data-id="element-691">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-692">
                Committee Name <span className="text-red-500" data-id="element-693">*</span>
              </label>
              <input type="text" id="name" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" placeholder={committeeType === 'sub' ? 'Enter team/sub-committee name' : 'Enter committee name'} value={name} onChange={e => setName(e.target.value)} required data-id="element-694" />
            </div>

            {/* Committee Description */}
            <div data-id="element-695">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-696">
                Description <span className="text-red-500" data-id="element-697">*</span>
              </label>
              <textarea id="description" rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" placeholder={committeeType === 'sub' ? 'Enter team/sub-committee description and objectives' : 'Enter committee description and objectives'} value={description} onChange={e => setDescription(e.target.value)} required data-id="element-698"></textarea>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-699">
              {/* Start Date */}
              <div data-id="element-700">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-701">
                  Start Date <span className="text-red-500" data-id="element-702">*</span>
                </label>
                <div className="relative" data-id="element-703">
                  <input type="date" id="startDate" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" value={startDate} onChange={e => setStartDate(e.target.value)} required data-id="element-704" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-705">
                    <Calendar size={18} className="text-gray-400" data-id="element-706" />
                  </div>
                </div>
              </div>

              {/* End Date */}
              <div data-id="element-707">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-708">
                  End Date
                </label>
                <div className="relative" data-id="element-709">
                  <input type="date" id="endDate" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" value={endDate} onChange={e => setEndDate(e.target.value)} data-id="element-710" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-711">
                    <Calendar size={18} className="text-gray-400" data-id="element-712" />
                  </div>
                </div>
              </div>

              {/* Meeting Frequency */}
              <div data-id="element-713">
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-714">
                  Meeting Frequency
                </label>
                <select id="frequency" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" value={frequency} onChange={e => setFrequency(e.target.value)} data-id="element-715">
                  <option value="" data-id="element-716">Select Frequency</option>
                  <option value="weekly" data-id="element-717">Weekly</option>
                  <option value="biweekly" data-id="element-718">Bi-weekly</option>
                  <option value="monthly" data-id="element-719">Monthly</option>
                  <option value="quarterly" data-id="element-720">Quarterly</option>
                  <option value="asNeeded" data-id="element-721">As Needed</option>
                </select>
              </div>

              {/* Status */}
              <div data-id="element-722">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-723">
                  Status <span className="text-red-500" data-id="element-724">*</span>
                </label>
                <select id="status" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" value={status} onChange={e => setStatus(e.target.value)} required data-id="element-725">
                  <option value="" data-id="element-726">Select Status</option>
                  <option value="active" data-id="element-727">Active</option>
                  <option value="planning" data-id="element-728">Planning</option>
                  <option value="onHold" data-id="element-729">On Hold</option>
                  <option value="completed" data-id="element-730">Completed</option>
                </select>
              </div>
            </div>

            {/* Members Section */}
            <div data-id="element-731">
              <h3 className="text-lg font-medium mb-3" data-id="element-732">Committee Members</h3>

              {/* Current Members */}
              {members.length > 0 && <div className="mb-4" data-id="element-733">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-734">
                    Current Members
                  </h4>
                  <div className="overflow-x-auto" data-id="element-735">
                    <table className="w-full" data-id="element-736">
                      <thead data-id="element-737">
                        <tr className="bg-gray-50" data-id="element-738">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-739">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-740">
                            Role
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-741">
                            Department
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-742">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-743">
                        {members.map(member => <tr key={member.id} data-id="element-744">
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-745">
                              <div className="flex items-center" data-id="element-746">
                                <div className="h-8 w-8 bg-gray-700 rounded-full flex items-center justify-center text-white font-medium mr-3" data-id="element-747">
                                  {member.avatar}
                                </div>
                                <span className="font-medium" data-id="element-748">
                                  {member.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-749">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${member.role === 'Chairperson' ? 'bg-blue-100 text-blue-800' : member.role === 'Secretary' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-750">
                                {member.role}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-751">
                              {member.department}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap" data-id="element-752">
                              <button type="button" onClick={() => removeMember(member.id)} className="text-red-500 hover:text-red-700" data-id="element-753">
                                <X size={16} data-id="element-754" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Add Member Form */}
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-755">
                <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-756">
                  Add Member
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-757">
                  {/* Member Selection */}
                  <div data-id="element-758">
                    <label htmlFor="member" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-759">
                      Select Member
                    </label>
                    <select id="member" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-760">
                      <option value="" data-id="element-761">Select a User</option>
                      {filteredUsers.map(user => <option key={user.id} value={user.id} data-id="element-762">
                          {user.name} ({user.department})
                        </option>)}
                    </select>
                  </div>

                  {/* Role Selection */}
                  <div data-id="element-763">
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-764">
                      Role
                    </label>
                    <select id="role" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" data-id="element-765">
                      <option value="" data-id="element-766">Select Role</option>
                      {roles.map((role, index) => <option key={index} value={role} data-id="element-767">
                          {role}
                        </option>)}
                    </select>
                  </div>

                  {/* Add Button */}
                  <div className="flex items-end" data-id="element-768">
                    <button type="button" className="w-full px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors flex items-center justify-center" onClick={() => {
                    const selectElement = document.getElementById('member') as HTMLSelectElement;
                    const selectedUserId = Number(selectElement.value);
                    const roleElement = document.getElementById('role') as HTMLSelectElement;
                    const selectedRole = roleElement.value;
                    if (selectedUserId && selectedRole) {
                      const selectedUser = availableUsers.find(u => u.id === selectedUserId);
                      if (selectedUser) {
                        addMember(selectedUser, selectedRole);
                        // Reset selections
                        selectElement.value = '';
                        roleElement.value = '';
                      }
                    }
                  }} data-id="element-769">
                      <Plus size={18} className="mr-1" data-id="element-770" />
                      Add Member
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-4" data-id="element-771">
              <Link to={`/committees/${committeeId}`} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-id="element-772">
                Cancel
              </Link>
              <button type="submit" className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-773">
                <Save size={18} className="inline mr-2" data-id="element-774" />
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>;
}